import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Home, CalendarDays, UserRound, Grid2X2, Heart, Radio, Church, MapPin, Bell, X } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { getMemberByUser } from '@/lib/memberLookup';
import { useUnreadNotifications } from '@/hooks/useUnreadNotifications';
import { createPageUrl } from '@/utils';
import ScrollToTopButton from '@/components/ScrollToTopButton';

const hiddenNavPages = [
  'AdminDashboard', 'AdminMembros', 'AdminBanners', 'AdminAgenda',
  'AdminOracoes', 'AdminIgreja', 'AdminCultos', 'AdminGrupos',
  'AdminGaleria', 'AdminVideos', 'AdminComunicacao', 'AdminAvaliacoes',
  'AdminPermissoes', 'AdminMinisterios', 'AdminRankingMBV', 'AdminQRCodeMBV',
  'AdminInscricoes', 'CadastroMembro', 'InscricaoEvento',
];

const quickAccessLinks = [
  { label: 'Agenda', page: 'Agenda', icon: CalendarDays },
  { label: 'Pedido de oração', page: 'PedidoOracao', icon: Heart },
  { label: 'Transmissão', page: 'Transmissao', icon: Radio },
  { label: 'Nossos cultos', page: 'NossosCultos', icon: Church },
  { label: 'Localização', page: 'Localizacao', icon: MapPin },
];

const desktopLinks = [
  { label: 'Início', page: 'Home' },
  ...quickAccessLinks.map(({ label, page }) => ({ label, page })),
  { label: 'Sobre', page: 'QuemSomos' },
];

const mobileLinks = [
  { label: 'Início', page: 'Home', icon: Home },
  { label: 'Agenda', page: 'Agenda', icon: CalendarDays },
  { label: 'Menu', menu: true, icon: Grid2X2 },
];

const publicPages = new Set([
  'Agenda', 'Avisos', 'Avaliacoes', 'Biblia', 'CartaoMembro', 'Doacoes',
  'EditarPerfil', 'Galeria', 'Grupos', 'Hinos', 'Localizacao', 'Mensagens', 'NossosCultos',
  'PedidoOracao', 'Perfil', 'QuemSomos', 'Transmissao', 'Videos',
  'CalendarioIntegrado', 'RankingMBV',
  'AdminDashboard', 'Relatorios',
]);

const teamPages = new Set([
  'EquipeLouvor', 'EquipeKidsMBV', 'EquipeMidia', 'EquipeDanca', 'EquipeRecepcao',
  'IntegrantesLouvor', 'IntegrantesKidsMBV', 'IntegrantesMidia', 'IntegrantesDanca', 'IntegrantesRecepcao',
  'EscalasLouvor', 'EscalasKidsMBV', 'EscalasMidia', 'EscalasDanca', 'EscalasRecepcao',
  'CalendarioDisponibilidade', 'CalendarioDisponibilidadeKidsMBV',
  'CalendarioDisponibilidadeMidia', 'CalendarioDisponibilidadeDanca',
  'CalendarioDisponibilidadeRecepcao', 'StaffCulto', 'CalendarioGrupo',
]);

export default function Layout({ children, currentPageName }) {
  const isAdminPage = currentPageName?.startsWith('Admin');
  const showNav = !isAdminPage && !hiddenNavPages.includes(currentPageName);
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [quickMenuOpen, setQuickMenuOpen] = React.useState(false);
  React.useEffect(() => setQuickMenuOpen(false), [currentPageName]);
  const { data: memberData } = useQuery({
    queryKey: ['memberData', user?.id, user?.email],
    queryFn: () => getMemberByUser(user),
    enabled: showNav && isAuthenticated && !!user,
  });
  const { data: unreadNotifications = [] } = useUnreadNotifications(showNav && isAuthenticated ? user : null, memberData?.id || null);
  const accountPhoto = isAuthenticated ? memberData?.photo_url : null;
  const openNotifications = () => {
    if (currentPageName === 'Home') {
      window.dispatchEvent(new Event('mbv:open-notifications'));
    } else {
      navigate(`${createPageUrl('Home')}?notifications=1`);
    }
  };

  return (
    <div className={`mbv-shell${showNav ? ' mbv-shell-with-nav' : ''}`}>
      {showNav && (
        <header className="mbv-site-header">
          <Link className="mbv-brand" to={createPageUrl('Home')} aria-label="MBV Igreja, início">
            <img src="/mbv-natal-icon-192.png" alt="MBV Igreja" />
          </Link>
          <nav className="mbv-desktop-nav" aria-label="Navegação principal">
            {desktopLinks.map(({ label, page }) => (
              <Link key={page} className={currentPageName === page ? 'is-active' : ''} to={createPageUrl(page)}>
                {label}
              </Link>
            ))}
          </nav>
          <div className="mbv-header-actions">
            <button type="button" className="mbv-header-bell" onClick={openNotifications} aria-label="Abrir notificações">
              <Bell size={21} />
              {unreadNotifications.length > 0 && <span className="mbv-notification-count">{unreadNotifications.length}</span>}
            </button>
            <Link className="mbv-header-visit mbv-account-link" to={createPageUrl('Perfil')}>
              {accountPhoto ? <img src={accountPhoto} alt="" /> : isAuthenticated ? <UserRound size={18} /> : null}
              <span>{isAuthenticated ? 'Perfil' : 'Entrar'}</span>
            </Link>
          </div>
        </header>
      )}

      {showNav && (
        <div className="mbv-mobile-topbar">
          <Link to={createPageUrl('Home')} className="mbv-mobile-brand" aria-label="MBV Igreja, início">
            <img src="/mbv-natal-icon-192.png" alt="MBV Igreja" />
          </Link>
          <div className="mbv-mobile-actions">
            <button type="button" className="mbv-notification-button" onClick={openNotifications} aria-label="Abrir notificações">
              <Bell size={21} />
              {unreadNotifications.length > 0 && <span className="mbv-notification-count">{unreadNotifications.length}</span>}
            </button>
            <Link className="mbv-mobile-account" to={createPageUrl('Perfil')} aria-label={isAuthenticated ? 'Abrir perfil' : 'Entrar'}>
              {accountPhoto ? <img src={accountPhoto} alt="" /> : <UserRound size={20} />}
              <span>{isAuthenticated ? 'Perfil' : 'Entrar'}</span>
            </Link>
          </div>
        </div>
      )}

      {publicPages.has(currentPageName) || teamPages.has(currentPageName) || isAdminPage
        ? <div className={`mbv-public-view mbv-page-${currentPageName.toLowerCase()}${teamPages.has(currentPageName) ? ' mbv-team-view' : ''}${currentPageName?.startsWith('Escalas') ? ' mbv-schedule-view' : ''}${isAdminPage ? ' mbv-admin-view' : ''}${currentPageName === 'Perfil' || currentPageName === 'EditarPerfil' ? ' mbv-profile-view' : ''}`}>{children}</div>
        : children}
      <ScrollToTopButton hasBottomNav={showNav} />

      {showNav && quickMenuOpen && (
        <div className="mbv-quick-sheet-backdrop" onClick={() => setQuickMenuOpen(false)}>
          <div className="mbv-quick-sheet" role="dialog" aria-modal="true" aria-label="Menu" onClick={(event) => event.stopPropagation()}>
            <div className="mbv-quick-sheet-heading"><h2>Menu</h2><button type="button" onClick={() => setQuickMenuOpen(false)} aria-label="Fechar menu"><X size={22} /></button></div>
            <div className="mbv-quick-sheet-links">
              {[{ label: 'Início', page: 'Home', icon: Home }, ...quickAccessLinks, { label: 'Sobre', page: 'QuemSomos', icon: Church }].map(({ label, page, icon: Icon }) => (
                <Link key={page} to={createPageUrl(page)} onClick={() => setQuickMenuOpen(false)}><Icon size={21} /><span>{label}</span><span aria-hidden="true">→</span></Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {showNav && (
        <nav className="mbv-bottom-nav" aria-label="Navegação do aplicativo">
          {mobileLinks.map(({ label, page, icon: Icon, menu }) => menu ? (
            <button key={label} type="button" className={quickMenuOpen ? 'is-active' : ''} onClick={() => setQuickMenuOpen((open) => !open)} aria-expanded={quickMenuOpen}>
              <Icon size={22} strokeWidth={quickMenuOpen ? 2.5 : 1.8} /><span>{label}</span>
            </button>
          ) : (
            <Link key={page} className={currentPageName === page ? 'is-active' : ''} to={createPageUrl(page)}>
              <Icon size={22} strokeWidth={currentPageName === page ? 2.5 : 1.8} /><span>{label}</span>
            </Link>
          ))}
        </nav>
      )}
    </div>
  );
}
